import {usePropsSelector} from '../../../state/hooks';
import {makePanelRefsSelector} from '../../../state/views/panelBankConfig/selectors';
import {LayedOutPanel} from '../../../util/panelTypes';
import {getTrimmedTitleFromAnyConfig} from '../../PanelTitle/getTitleFromConfig';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';

export const usePanelRefAndPanelBankSectionConfigRef = (panelId: string) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {panelId}
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const usePanelRefAndPanelBankSectionConfigRefByName = (
  panelDisplayName: string,
  panelSectionName?: string
) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {
      displayName: panelDisplayName,
      sectionName: panelSectionName,
    }
  );
  return panelRefAndPanelBankSectionConfigRef;
};

const getPanelLinkPathname = () => {
  const path = window.location.pathname;
  // Strip any existing panel path
  const panelIndex = path.indexOf('/panel/');
  return panelIndex !== -1
    ? path.substring(0, panelIndex)
    : path.replace(/\/+$/, '');
};

const getPanelLinkSearchParams = () =>
  new URLSearchParams(window.location.search);

/**
 * Returns a link to a full-screen panel.
 *
 * If panel.isAuto (was auto-generated and never edited),
 * the URL is based on the panel display name. Otherwise, the
 * URL is based on panel ID. Note that panel IDs are NOT stable
 * for auto panels, which is why we can only use ID-based URLs
 * for non-auto panels.
 */
export const getSharePanelLink = (
  panel: LayedOutPanel,
  panelBankSectionName: string
) => {
  const pathname = getPanelLinkPathname();
  const searchParams = getPanelLinkSearchParams();
  const panelType = panel.viewType;
  let newUrl = '';
  const panelDisplayName = getTrimmedTitleFromAnyConfig(panelType, panel);
  if (panel.isAuto && panelDisplayName) {
    searchParams.set('panelDisplayName', panelDisplayName);
    searchParams.set('panelSectionName', panelBankSectionName);
    newUrl = `${pathname}?${searchParams.toString()}`;
  } else {
    newUrl = getPanelLinkById(panel.__id__);
  }
  return newUrl;
};

/**
 * Returns an ID-based URL for a full-screen panel.
 * PLEASE NOTE THAT ID-BASED URLS SHOULD ONLY BE USED FOR NON-AUTO PANELS.
 */
export const getPanelLinkById = (panelId: string) => {
  const pathname = getPanelLinkPathname();
  const searchParams = getPanelLinkSearchParams();
  // if we're currently on a displayName-based URL, remove
  // those search params since they're no longer needed
  if (searchParams.get('panelDisplayName')) {
    searchParams.delete('panelDisplayName');
  }
  if (searchParams.get('panelSectionName')) {
    searchParams.delete('panelSectionName');
  }
  return `${pathname}/panel/${panelId}?${searchParams.toString()}`;
};
